import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { platformApi } from '../../api/platformApi';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export const PlatformLinks = () => {
    const { token } = useAuth();
    const [links, setLinks] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [linksData, tenantsData] = await Promise.all([
                platformApi.getAllLinks(token),
                platformApi.getTenants(token, true)
            ]);
            setLinks(linksData);
            setTenants(tenantsData);
        } catch (error) {
            toast.error("Error cargando datos");
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (id, newStatus) => {
        if (!window.confirm(`¿Cambiar estado a ${newStatus}?`)) return;
        try {
            await platformApi.updateLinkStatus(token, id, newStatus);
            toast.success(`Link ${newStatus}`);
            loadData();
        } catch (error) {
            toast.error("Error actualizando status");
        }
    };

    const getTenantName = (id) => {
        const t = tenants.find(x => x.id === id);
        return t ? (t.nombre || t.slug) : id.substring(0, 8) + '...';
    };

    const getTenantType = (id) => {
        const t = tenants.find(x => x.id === id);
        return t ? t.tenant_type : 'UNKNOWN';
    };

    // Filter out links involving END_USER just in case
    const filteredLinks = links.filter(link => {
        const typeA = getTenantType(link.company_tenant_id);
        const typeB = getTenantType(link.law_firm_tenant_id);
        return typeA !== 'END_USER' && typeB !== 'END_USER';
    });

    if (loading) return <div className="flex justify-center p-8"><Loader2 className="animate-spin" /></div>;

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Vínculos entre Tenants (B2B)</h3>
                <Button variant="outline" size="sm" onClick={loadData}><RefreshCw className="h-4 w-4" /></Button>
            </div>

            <div className="border rounded-md bg-white">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Empresa</TableHead>
                            <TableHead>Despacho</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Scopes</TableHead>
                            <TableHead className="text-right">Acciones</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filteredLinks.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">No hay conexiones B2B</TableCell>
                            </TableRow>
                        ) : (
                            filteredLinks.map(link => (
                                <TableRow key={link.id}>
                                    <TableCell className="font-medium">
                                        {getTenantName(link.company_tenant_id)}
                                        <div className="text-[10px] text-muted-foreground font-mono">{link.company_tenant_id.substring(0, 6)}...</div>
                                    </TableCell>
                                    <TableCell className="font-medium">
                                        {getTenantName(link.law_firm_tenant_id)}
                                        <div className="text-[10px] text-muted-foreground font-mono">{link.law_firm_tenant_id.substring(0, 6)}...</div>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant={link.status === "ACTIVE" ? "default" : "secondary"}>
                                            {link.status}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-xs">
                                        {Object.keys(link.scopes || {}).filter(k => link.scopes[k]).join(", ")}
                                    </TableCell>
                                    <TableCell className="text-right space-x-1">
                                        {link.status !== "ENDED" && (
                                            <>
                                                {link.status !== "ACTIVE" && (
                                                    <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleStatusChange(link.id, 'ACTIVE')}>Activar</Button>
                                                )}
                                                {link.status === "ACTIVE" && (
                                                    <Button size="sm" variant="outline" className="text-orange-600" onClick={() => handleStatusChange(link.id, 'PAUSED')}>Pausar</Button>
                                                )}
                                                <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleStatusChange(link.id, 'ENDED')}>Terminar</Button>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
};
